import React, { useState } from 'react';
import {
  IonInput,
  IonRow,
  IonCol,
  IonIcon,
  IonFab,
  IonFabButton,
  IonLabel,
  IonText,
  IonButton,
  IonSelect,
  IonSelectOption,
  IonLoading
} from '@ionic/react';
import './AccountForm.css';
import { saveOutline } from 'ionicons/icons';
import { AuthenticationRootState } from '../store/reducers/authentication';
import { connect } from 'react-redux';
import { User } from '../models';
import {
  updateLocalUserPatient,
  saveUserPatient
} from '../store/actions/authentication';
import EventBus from '../services/EventBus';
import { Controller, useForm, UseFormMethods } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import FormError from './FormError';
import { getUserSections, InputProps, Inputs } from './AccountFormInputsList';
import Patient, { PatientToUpdate } from '../models/Patient';
import DateInput from './DateInput';
import { useChangePasswordCallback } from '../hooks/msal.hooks';

const mapStateToProps = (
  state: { authentication: AuthenticationRootState },
  ownProps: any
) => {
  return {
    userState: state.authentication.user!,
    isRopc: state.authentication.isRopc
  };
};

const mapDispatchToProps = (dispatch: Function, ownProps: any) => ({
  updateLocalUserPatient: (patientAttributes: Object) =>
    dispatch(updateLocalUserPatient(patientAttributes)),
  saveUserPatient: (patient: PatientToUpdate) =>
    dispatch(saveUserPatient(patient))
});

type Props = { patient: Patient; onUpdated?: Function } & ReturnType<
  typeof mapStateToProps
> &
  ReturnType<typeof mapDispatchToProps>;

const AccountForm: React.FC<Props> = ({
  patient,
  onUpdated = () => {},
  userState,
  isRopc,
  updateLocalUserPatient,
  saveUserPatient
}) => {
  const [user] = useState<User>(userState);

  const [isUploading, setIsUploading] = useState(false);

  const changePassword = useChangePasswordCallback();

  let sections = getUserSections(patient, user);

  let defaultValues: { [key: string]: any } = {};

  sections.forEach((section) => {
    section.fields.forEach((field) => {
      defaultValues[field.name] = field.value || '';
    });
  });

  const hookForm = useForm<Inputs>({
    mode: 'onChange',
    defaultValues
  });

  sections = getUserSections(patient, user, hookForm);

  if (!user) {
    return null;
  }

  async function onClickSubmit(data: Inputs) {
    if (user) {
      const tmpPatient = new PatientToUpdate({
        id: patient.id,
        ...data,
        nir: data.nir.replace(/\D/g, '')
      });

      setIsUploading(true);

      await saveUserPatient(tmpPatient);

      if (tmpPatient.id === userState.patient.id) {
        updateLocalUserPatient(tmpPatient);
      }

      onUpdated();
      setIsUploading(false);
      EventBus.success('Informations sauvegardées.');
    } else {
      EventBus.error('');
    }
  }

  return (
    <>
      <IonLoading isOpen={isUploading} />
      <form className="wrapper wrapper--large">
        <div className="ion-padding-bottom">
          {sections.map((section) => (
            <FormSection
              {...section}
              user={userState}
              key={section.name}
              hookForm={hookForm}
            />
          ))}
        </div>
        {!isRopc && (
          <IonButton onClick={changePassword}>
            Modifier mon mot de passe
          </IonButton>
        )}
      </form>

      <IonFab vertical="bottom" horizontal="end" slot="fixed">
        <IonButton
          type="submit"
          onClick={hookForm.handleSubmit(onClickSubmit)}
          className="ion-hide-md-down"
        >
          <IonIcon icon={saveOutline} slot="start" /> Enregistrer mes
          modifications
        </IonButton>
        <IonFabButton
          type="submit"
          onClick={hookForm.handleSubmit(onClickSubmit)}
          className="ion-hide-md-up"
        >
          <IonIcon icon={saveOutline} />
        </IonFabButton>
      </IonFab>
    </>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(AccountForm);

type FormSectionProps = {
  hookForm: UseFormMethods<Inputs>;
  user: User;
};

const FormSection: React.FC<InputProps & FormSectionProps> = ({
  name,
  fields,
  hookForm,
  user
}) => {
  function showError(fieldName: string) {
    return (
      (hookForm.errors as any)[fieldName] && (
        <ErrorMessage
          errors={hookForm.errors}
          name={fieldName}
          render={({ message }) => <FormError message={message as string} />}
        />
      )
    );
  }
  let mutual = '';
  if (user && user.patient && user.patient.documents.mutual) {
    mutual = 'Votre mutuelle a bien été renseignée.';
  }
  return (
    <div className="account-form-section">
      <IonRow className="ion-align-items-center">
        <IonCol>
          <h3>{name}</h3>
        </IonCol>
      </IonRow>
      <div className="account-form-section__fields">
        {fields.map((field) => (
          <div key={field.name} className="account-input">
            <IonLabel position="stacked">
              {field.label}
              {field.rules ? <IonText color="danger">*</IonText> : null}
            </IonLabel>
            <Controller
              control={hookForm.control}
              name={field.name}
              rules={field.rules}
              render={({ onChange, onBlur, ref, value, name }) =>
                field.type === 'date' ? (
                  <div className="account-form-section__date">
                    <DateInput
                      {...{ onChange, onBlur, value, name }}
                      placeholder="Date de naissance*"
                      disabled={field.disabled}
                      className="flat"
                    />
                  </div>
                ) : field.type === 'file' ? (
                  <>
                    <input
                      name={name}
                      type="file"
                      accept=".pdf, image/png, image/jpeg"
                      onChange={(e) => {
                        onChange(e.target.files ? e.target.files[0] : null);
                      }}
                      ref={ref}
                      disabled={field.disabled}
                    />
                    {name === 'mutual' && !!mutual && (
                      <div className="account-form-section__note">{mutual}</div>
                    )}
                  </>
                ) : field.type === 'select' ? (
                  <IonSelect
                    name={name}
                    okText="Confirmer"
                    cancelText="Annuler"
                    onIonChange={(e) => onChange(e.detail.value!)}
                    value={value}
                    class="flat"
                    interface="popover"
                    ref={ref}
                    disabled={field.disabled}
                  >
                    {field.options?.map((option) => (
                      <IonSelectOption key={option.value} value={option.value}>
                        {option.label}
                      </IonSelectOption>
                    ))}
                  </IonSelect>
                ) : (
                  <IonInput
                    name={name}
                    required
                    type={field.type!}
                    value={value ? value : null}
                    placeholder={field.placeholder}
                    class="flat"
                    onIonChange={(e) => {
                      onChange(e.detail.value!);
                    }}
                    ref={ref}
                    autocapitalize="on"
                    disabled={field.disabled}
                  />
                )
              }
            />

            <div className="form-error-wrapper">{showError(field.name)}</div>
          </div>
        ))}
      </div>
    </div>
  );
};
